import React, { useState, useEffect } from 'react';
import Header from '../src/Components/Header';
import MainHeader from '../src/Components/MainHeader';
import Footer from '../src/Components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { 
    FaLink, FaShareAlt, FaMoneyBillWave, FaCopy, FaWhatsapp, FaEnvelope, FaArrowRight,
    FaCheckCircle, FaBolt, FaInfinity
} from 'react-icons/fa';
import './AmbassadorPage.css'; 
import SEOHelmet from '../src/Components/SEOHelmet';

const AmbassadorPage = () => {
    const [generateRefInput, setGenerateRefInput] = useState('');
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init({ offset: 80, duration: 600 });
    }, []);

    const validateInput = (input) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{10}$/;
        return emailRegex.test(input) || phoneRegex.test(input);
    };

    const handleGenerateLink = () => {
        setError('');
        if(!generateRefInput) {
            setError('Please enter your email or phone number first.');
            return;
        }
        if(!validateInput(generateRefInput)) {
            setError('Please enter a valid email or 10-digit phone number.');
            return;
        }
        const link = `${window.location.origin}/internship?ref=${encodeURIComponent(generateRefInput)}`;
        setGeneratedLink(link);
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsAppShare = () => {
        if(!generatedLink) return;
        const message = `Hey! 🚀 I just found an incredible opportunity to get hands-on experience and build industrial tech projects with Octalbees. This is exactly what we need to stand out and kickstart our careers! Don't miss out, secure your spot using my invite link: ${generatedLink}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    return (
        <div style={{ backgroundColor: '#0a0118', minHeight: '100vh', width: '100%' }}>
            <Header />
            <MainHeader />
            <div className="ambassador-page">
                <SEOHelmet
                    title="Ambassador Program – Earn by Referring Friends"
                    description="Join the Octalbees Ambassador Program. Share your referral link, help friends build industrial tech skills, and earn ₹250 for every successful enrollment."
                    path="/ambassador"
                />
                
                {/* Hero Section */}
                <section className="amb-hero">
                    <div className="amb-hero-bg">
                        <div className="amb-hero-glow amb-glow-1"></div>
                        <div className="amb-hero-glow amb-glow-2"></div>
                        <div className="amb-hero-grid"></div>
                    </div>
                    <div className="container">
                        <div className="amb-hero-content">
                            <div className="amb-badge" data-aos="fade-down">
                                <span className="amb-badge-pulse"></span>
                                Ambassador Program
                            </div>
                            <h1 className="amb-hero-title" data-aos="fade-up">
                                Empower Your Peers.
                                <span className="amb-gradient-text"> Grow Together.</span>
                            </h1>
                            <p className="amb-hero-sub" data-aos="fade-up" data-aos-delay="80">
                                Invite your peers to build industrial skills, and earn <strong>₹250</strong> for every friend who joins the journey.
                            </p>
                            <div className="amb-hero-actions" data-aos="fade-up" data-aos-delay="150">
                                <a href="#amb-generate" className="amb-cta-primary">
                                    Start Referring <FaArrowRight />
                                </a>
                            </div>
                            <div className="amb-hero-chips" data-aos="fade-up" data-aos-delay="200">
                                <div className="amb-chip">
                                    <FaCheckCircle /> Free to Join
                                </div>
                                <div className="amb-chip">
                                    <FaBolt /> Direct UPI Payouts
                                </div>
                                <div className="amb-chip">
                                    <FaInfinity /> Unlimited Referrals
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works — 3-Column Card Grid */}
                <section className="amb-how-section">
                    <div className="container">
                        <div className="amb-section-top" data-aos="fade-up">
                            <span className="amb-label">Simple Process</span>
                            <h2 className="amb-heading">How It Works</h2>
                            <p className="amb-subtext">Three simple steps to start earning.</p>
                        </div>
                        <div className="amb-steps-grid">
                            <div className="amb-step" data-aos="fade-up" data-aos-delay="50">
                                <div className="amb-step-num">1</div>
                                <div className="amb-step-icon-wrap"><FaLink /></div>
                                <h4>Generate Invite</h4>
                                <p>Use your registered email or phone number to create a personalized invitation link.</p>
                            </div>
                            <div className="amb-step" data-aos="fade-up" data-aos-delay="150">
                                <div className="amb-step-num">2</div>
                                <div className="amb-step-icon-wrap"><FaShareAlt /></div>
                                <h4>Share the Opportunity</h4>
                                <p>Send your link to friends or college groups who need industrial project experience.</p>
                            </div>
                            <div className="amb-step" data-aos="fade-up" data-aos-delay="250">
                                <div className="amb-step-num">3</div>
                                <div className="amb-step-icon-wrap"><FaMoneyBillWave /></div>
                                <h4>Get Appreciated</h4>
                                <p>When they join, we send <strong>₹250</strong> directly to your bank/UPI as a thank you.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Link Generator */}
                <section className="amb-gen-section" id="amb-generate">
                    <div className="container">
                        <div className="amb-gen-card" data-aos="fade-up">
                            <div className="amb-gen-border-glow"></div>
                            <div className="amb-gen-inner">
                                <h3>Generate Your Invite Link</h3>
                                <p>Enter your registered details to create a personalized referral link.</p>
                                
                                <div className="amb-form-row">
                                    <input 
                                        type="text" 
                                        placeholder="Email or 10-digit phone number" 
                                        value={generateRefInput} 
                                        onChange={(e) => {
                                            setGenerateRefInput(e.target.value);
                                            setError('');
                                            setGeneratedLink('');
                                        }}
                                        className="amb-input"
                                    />
                                    <button onClick={handleGenerateLink} className="amb-copy-btn">
                                        {copied ? <><FaCopy /> Copied!</> : <><FaLink /> Copy Link</>}
                                    </button>
                                </div>
                                {error && <p className="amb-error">{error}</p>}
                                {generatedLink && (
                                    <div className="amb-wa-wrap">
                                        <button onClick={handleWhatsAppShare} className="amb-wa-btn">
                                            <FaWhatsapp /> Share on WhatsApp
                                        </button>
                                    </div>
                                )}
                                <p className="amb-note">
                                    *Tokens of appreciation are sent within 48 hours of successful verification.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Help */}
                <section className="amb-help">
                    <div className="container">
                        <div className="amb-help-card" data-aos="fade-up">
                            <div className="amb-help-icon"><FaEnvelope /></div>
                            <div className="amb-help-text">
                                <h4>Need Help?</h4>
                                <p>Questions about the Ambassador Program? We're here for you.</p>
                            </div>
                            <a href="mailto:ambassador@octalbees.com" className="amb-help-link">
                                ambassador@octalbees.com
                            </a>
                        </div>
                    </div>
                </section>

            </div>
            <Footer />
        </div>
    );
};

export default AmbassadorPage;
